import {
  AuthenticationResult,
  CacheLookupPolicy,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { ITollingAdminAccessObject } from 'types/apiTypes';
import msalInstance from 'utils/msalInstance';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { jwtDecode, JwtPayload } from 'jwt-decode';

const VITE_AUTH_SCOPES: string[] = ((import.meta.env.VITE_AUTH_SCOPES || '') as string).split(' ');

interface AuthStoreProps {
  token: AuthenticationResult | null;
  tollingAdminAccessObject: ITollingAdminAccessObject | null;
  tokenAccessRoles: string[];
}

interface AuthStoreActions {
  clearToken: () => void;
  acquireTokenSso: () => Promise<AuthenticationResult | null>;
  acquireToken: () => Promise<AuthenticationResult | null>;
  setTollingAdminAccessObject: (newTollingAdminAccess: ITollingAdminAccessObject | null) => void;
}

interface JwtPayloadWithRoles extends JwtPayload {
  roles: string[];
}

const getTokenRoles = (accessToken: string) => {
  const decodedToken = jwtDecode<JwtPayloadWithRoles>(accessToken);
  return decodedToken?.roles ?? [];
};

const useAuthStore = create(
  persist<AuthStoreProps & AuthStoreActions>(
    (set, get) => ({
      token: null,
      tollingAdminAccessObject: null,
      tokenAccessRoles: [],
      clearToken: () => {
        set({ token: null, tokenAccessRoles: [] });
      },
      acquireTokenSso: async () => {
        try {
          const loginHint = localStorage.getItem('login-hint') ?? undefined;
          const newToken = await msalInstance.ssoSilent({
            loginHint,
            scopes: [...VITE_AUTH_SCOPES],
          });
          set({ token: newToken, tokenAccessRoles: getTokenRoles(newToken.accessToken) });
        } catch (error) {
          console.error('acquireTokenSso error', error);
          set({ token: null, tokenAccessRoles: [] });
        }
        return get().token;
      },
      acquireToken: async () => {
        try {
          const account = msalInstance.getAllAccounts()[0];
          if (!account) {
            set({ token: null, tokenAccessRoles: [] });
            return null;
          }
          const newToken = await msalInstance.acquireTokenSilent({
            scopes: [...VITE_AUTH_SCOPES],
            account,
            cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
          });
          set({ token: newToken, tokenAccessRoles: getTokenRoles(newToken.accessToken) });
          localStorage.setItem('login-hint', newToken.account.username);
        } catch (error) {
          if (!(error instanceof InteractionRequiredAuthError)) {
            console.error('acquireToken error', error);
          }
          set({ token: null, tokenAccessRoles: [] });
        }
        return get().token;
      },
      setTollingAdminAccessObject: (newTollingAdminAccess: ITollingAdminAccessObject | null) => {
        set({ tollingAdminAccessObject: newTollingAdminAccess });
      },
    }),
    {
      name: 'authStorage',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

export default useAuthStore;
